import { AgGridReact } from 'ag-grid-react';
import { AllCommunityModule, ModuleRegistry } from 'ag-grid-community';
import { useState, useMemo, useCallback, useEffect } from 'react';
import { useNavigate, useSearchParams } from "react-router-dom";
import { Badge, Dropdown, DropdownItem, DropdownMenu, DropdownToggle } from "reactstrap";
import SidebarLayout from '../../../../layouts/SidebarLayout';
import sidebar from '../../utils/sidebar';
import PageSubHeader from '../../../../components/Layouts/PageSubHeader';
import DefaultButton from '../../../../components/Buttons/Default';
import IconButton from '../../../../components/Buttons/IconButton';
import { IoCreateOutline, IoCloudUploadOutline, IoTrashOutline } from 'react-icons/io5';
import { BsTrash3, BsDownload } from 'react-icons/bs';
import { TfiFilter } from 'react-icons/tfi';
import { PiDotsThreeOutlineVerticalFill } from 'react-icons/pi';
import { del, get, getBlob } from '../../../../utils/helpers/api';
import { toast } from 'react-toastify';
import { useTranslation } from 'react-i18next';
import DefaultPagination from '../../../../components/Pagination';
import ConfirmationModal from '../../../../components/Modals/Confirmation';
import FiltersDrawer from './Drawers/Filters';
import './styles.css';
import AgGridTable from '../../../../components/Tables/AgGrid';
import { useCurrency } from '../../../../utils/helpers/currency';

ModuleRegistry.registerModules([AllCommunityModule]);

function ActionsCellRenderer({ data, context }) {
  return (
    <div className="d-flex align-items-center gap-2 h-100">
      <IconButton
        onClick={(e) => {
          e.stopPropagation();
          context.navigate(`/purchases/suppliers/edit/${data.id}`, { state: { supplierData: data } });
        }}
        size="sm"
        outline
        color="secondary"
      >
        <IoCreateOutline />
      </IconButton>

      <IconButton
        onClick={(e) => {
          e.stopPropagation();
          context.setSupplierToDelete(data.id);
        }}
        size="sm"
        outline
        color="danger"
      >
        <BsTrash3 />
      </IconButton>
    </div>
  );
}

function SuppliersPage() {
  const { t } = useTranslation();
  const currency = useCurrency();
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const [rowData, setRowData] = useState([]);
  const [currentPage, setCurrentPage] = useState(1);
  const [pageSize] = useState(20);
  const [totalPages, setTotalPages] = useState(1);
  const [totalRecords, setTotalRecords] = useState(0);
  const [loading, setLoading] = useState(false);
  const [supplierToDelete, setSupplierToDelete] = useState(null);
  const [moreOpen, setMoreOpen] = useState(false);
  const [filtersOpen, setFiltersOpen] = useState(false);

  const FILTER_KEYS = ['name', 'email', 'phone_number', 'added_after', 'added_before'];

  const [filters, setFilters] = useState(() => {
    const initial = {};
    FILTER_KEYS.forEach(key => {
      const val = searchParams.get(key);
      if (val) initial[key] = val;
    });
    return initial;
  });

  const filtersCount = Object.values(filters).filter(Boolean).length;

  const getSuppliers = useCallback(async () => {
    setLoading(true);
    let query = '/purchases/suppliers?page=' + currentPage + '&page_size=' + pageSize;
    const activeFilters = Object.fromEntries(Object.entries(filters).filter(([, v]) => v));
    if (Object.keys(activeFilters).length) {
      query += '&' + new URLSearchParams(activeFilters).toString();
    }
    const data = await get(query);
    if (data?.suppliers?.data) {
      setRowData(data.suppliers.data);
      setTotalPages(data.suppliers.last_page);
      setTotalRecords(data.suppliers.total);
    }
    setLoading(false);
  }, [currentPage, pageSize, filters]);

  useEffect(() => {
    getSuppliers();
  }, [getSuppliers]);

  const handleApplyFilters = (filtersData) => {
    const activeFilters = Object.fromEntries(Object.entries(filtersData).filter(([, v]) => v));
    setFilters(activeFilters);
    setSearchParams(activeFilters);
    setCurrentPage(1);
    setFiltersOpen(false);
  };

  const exportSuppliers = async () => {
    await getBlob('/purchases/suppliers/export')
      .then(blob => {
        if (!blob) throw new Error('export failed');
        const url = window.URL.createObjectURL(blob);
        const a = document.createElement('a');
        a.href = url;
        a.download = `suppliers-${new Date().toISOString()}.csv`;
        document.body.appendChild(a);
        a.click();
        a.remove();
      })
      .catch(() => {
        toast.error(t('common.exportError'));
      });
  };

  const deleteSupplier = async () => {
    const data = await del('/purchases/suppliers/delete/' + supplierToDelete);
    if (data.success) {
      toast.success(data.message || t('purchases.supplierDeleted'));
      getSuppliers();
    } else {
      toast.error(data.message || t('common.somethingWentWrong'));
    }
    setSupplierToDelete(null);
  };

  const columnDefs = useMemo(() => [
    { 
      field: 'company_name', 
      headerName: t('common.name'), 
      minWidth: 200, 
      flex: 1, 
      sortable: true,
      valueFormatter: (params) => {
        if(params.data.company_name) {
          return params.data.company_name;
        } else {
          return `${params.data.firstname ?? ''} ${params.data.lastname ?? ''}`;
        }
      }
    },
    { field: 'email', headerName: t('common.email'), minWidth: 180, sortable: true },
    { field: 'phone_number', headerName: t('common.phone'), minWidth: 150, sortable: true },
    { 
      field: 'outstanding_balance', 
      headerName: t('accounting.outstandingBalances'), 
      minWidth: 140, 
      sortable: true,
      valueFormatter: (params) => {
        const value = params.value;
        if (value === null || value === undefined) return '';
        return `${value.toFixed(2)} ${currency}`;
      }
    },
    {
      headerName: t('common.actions'),
      minWidth: 100,
      sortable: false,
      resizable: false,
      cellRenderer: ActionsCellRenderer,
    },
  ], [t, currency]);

  const defaultColDef = useMemo(() => ({
    resizable: true,
  }), []);

  const gridContext = useMemo(() => ({ navigate, setSupplierToDelete }), [navigate]);

  const onRowClicked = (event) => {
    if (event.event?.target?.closest('button')) return;
    navigate(`/purchases/suppliers/view/${event.data.id}`, { state: { supplierData: event.data } });
  };

  return (
    <SidebarLayout sidebar={sidebar}>
      <div className="page-with-subheader">
        <PageSubHeader title={t("purchases.suppliers")} onReload={getSuppliers} dataTotal={totalRecords}>
          <button className="subheader-btn" type="button" onClick={() => setFiltersOpen(true)}>
            <TfiFilter size={13} />
            {t("common.filter")}
            {filtersCount > 0 && (
              <Badge color="primary" className="filter-badge">{filtersCount}</Badge>
            )}
          </button>
          <div className="subheader-divider" />
          <DefaultButton
            color="primary"
            size="sm"
            onClick={() => navigate('/purchases/suppliers/create')}
          >
            {t("purchases.newSupplier")}
          </DefaultButton>
          <Dropdown isOpen={moreOpen} toggle={() => setMoreOpen(o => !o)} size="sm">
            <DropdownToggle outline color="secondary">
              <PiDotsThreeOutlineVerticalFill />
            </DropdownToggle>
            <DropdownMenu end>
              <DropdownItem onClick={exportSuppliers}>
                <BsDownload size={15} /> {t("common.export")}
              </DropdownItem>
            </DropdownMenu>
          </Dropdown>
        </PageSubHeader>

        <div className="subheader-page-content">
          <AgGridTable 
            rowData={rowData}
            columnDefs={columnDefs}
            defaultColDef={defaultColDef}
            loading={loading}
            context={gridContext}
            onRowClicked={onRowClicked}
          />

          <DefaultPagination
            currentPage={currentPage}
            totalPages={totalPages}
            onPageChange={setCurrentPage}
          />
        </div>
      </div>

      <FiltersDrawer
        isOpen={filtersOpen}
        toggle={() => setFiltersOpen(false)}
        filters={filters}
        applyFilters={handleApplyFilters}
      />

      <ConfirmationModal
        isOpen={supplierToDelete !== null}
        title={t("purchases.deleteSupplier")}
        message={t("purchases.deleteSupplierConfirm")}
        onConfirm={deleteSupplier}
        toggle={() => setSupplierToDelete(null)}
      />
    </SidebarLayout>
  );
}

export default SuppliersPage;
